import anylogger from 'anylogger';

const ignoreModuleMessages = {
  "PORTABLEREGISTRY:": ["Unable to determine runtime Event type, cannot inspect frame_system::EventRecord", "Unable to determine runtime Call type, cannot inspect sp_runtime::generic::unchecked_extrinsic::UncheckedExtrinsic"]
};
function suppressPortableRegistryConsoleWarnings() {
  /* eslint-disable-next-line no-console */
  const originalWarn = console.warn;

  /* eslint-disable-next-line no-console */
  console.warn = (...data) => {
    const [, dataModule, dataMessage] = data;
    const ignoreMessages = typeof dataModule === "string" && ignoreModuleMessages[dataModule];
    if (Array.isArray(ignoreMessages) && ignoreMessages.includes(dataMessage)) return;
    if (data[0] === "Unable to map Bytes to a lookup index") return;
    if (data[0] === "Unable to map u16 to a lookup index") return;
    if (data[0] === "Unable to map u32 to a lookup index") return;
    if (data[0] === "Unable to map u64 to a lookup index") return;
    if (data[0] === "Unable to map u128 to a lookup index") return;
    originalWarn(...data);
  };
}

/**
 * A function which allows you to decode, modify and then re-encode the type metadata for a chain.
 *
 * All of the encoding/decoding logic depends on the Codec implementation in the subsquid codebase
 * (@subsquid/substrate-metadata/src/codec.ts).
 * This subsquid implementation is capable of both decoding a blob of metadata as well as taking the
 * decoded metadata and re-encoding it back into its on-chain format.
 *
 * An example use-case for this function is for decoding balances on many chains.
 * Loading up the full ~0.5MB metadata blob for each chain just to decode the one balance type has
 * an unacceptable performance overhead on the frontend, which completely prevents us from doing
 * multi-chain balances.
 *
 * By filtering the full metadata blob down to just the types needed for balance decoding, we can
 * reduce the overhead by several orders of magnitude, solving the performance limitation.
 *
 * What's also neat is that with our filtered down metadata we are still free to use any of the
 * available SCALE decoding libraries on the frontend, as long as they can load up a metadata blob.
 */
let mutateMetadata = async () => {
  // eslint-disable-next-line no-console
  console.error("mutateMetadata depends on nodejs internals and cannot be used in a browser environment");
  return null;
};

var packageJson = {
	name: "@talismn/mutate-metadata",
	version: "0.0.0-pr1304-20240208214653",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/mutate-metadata",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-mutate-metadata.cjs.js",
	module: "dist/talismn-mutate-metadata.esm.js",
	browser: {
		"./dist/talismn-mutate-metadata.esm.js": "./dist/talismn-mutate-metadata.browser.esm.js"
	},
	exports: {
		".": {
			module: {
				browser: "./dist/talismn-mutate-metadata.browser.esm.js",
				"default": "./dist/talismn-mutate-metadata.esm.js"
			},
			"default": "./dist/talismn-mutate-metadata.cjs.js"
		},
		"./package.json": "./package.json"
	},
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@subsquid/scale-codec": "^1.1.1",
		"@subsquid/substrate-metadata": "^2.3.0",
		anylogger: "^1.0.11"
	},
	devDependencies: {
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		eslint: "^8.52.0",
		jest: "^28.1.0",
		"ts-jest": "^28.0.2",
		typescript: "^5.2.2"
	},
	preconstruct: {
		exports: {
			envConditions: [
				"browser"
			]
		}
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

const metadataIsV14 = metadata => {
  if (metadata.__kind === "V0" || metadata.__kind === "V1" || metadata.__kind === "V2" || metadata.__kind === "V3" || metadata.__kind === "V4" || metadata.__kind === "V5" || metadata.__kind === "V6" || metadata.__kind === "V7" || metadata.__kind === "V8" || metadata.__kind === "V9" || metadata.__kind === "V10" || metadata.__kind === "V11" || metadata.__kind === "V12" || metadata.__kind === "V13") {
    // we can't parse metadata < v14
    //
    // as of v14 the type information required to interact with a chain is included in the chain metadata
    // https://github.com/paritytech/substrate/pull/8615
    //
    // before this change, the client needed to already know the type information ahead of time
    return false;
  }
  return true;
};
const filterMetadataPalletsAndItems = (metadata, palletsAndItems, keepExtraTypes) => {
  // remove pallets we don't care about
  metadata.value.pallets = metadata.value.pallets.filter(pallet =>
  // keep this pallet if it's listed in `palletsAndItems`
  palletsAndItems.some(({
    pallet: palletFilter
  }) => palletFilter(pallet)));

  // remove fields we don't care about from each pallet, and extract types for each storage item we care about
  const items = palletsAndItems.flatMap(({
    pallet: palletFilter,
    items
  }) => {
    const pallet = metadata.value.pallets.find(palletFilter);
    if (!pallet || !pallet.storage) {
      log.warn("Failed to find pallet", palletFilter);
      return [];
    }

    // remove fields we don't care about
    pallet.events = undefined;
    pallet.calls = undefined;
    pallet.errors = undefined;
    pallet.constants = [];

    // filter and extract storage items we care about
    pallet.storage.items = pallet.storage.items.filter(item => items.some(itemFilter => itemFilter(item)));
    return pallet.storage.items;
  });

  // this is a set of type ids which we plan to keep in our mutated metadata
  // anything not in this set will be deleted
  // we start off with just the types of the state calls we plan to make,
  // then we run those types through a function (addDependentTypes) which will also include
  // all of the types which those types depend on - recursively
  const keepTypes = new Set(items.flatMap(item => [
  // each type can be either "Plain" or "Map"
  // if it's "Plain" we only need to get the value type
  // if it's a "Map" we want to keep both the key AND the value types
  item?.type.__kind === "Map" && item.type.key, item?.type.value]).filter(type => typeof type === "number"));

  // recursively find all the types which our keepTypes depend on and add them to the keepTypes set
  addDependentTypes(metadata, keepTypes, [...keepTypes]);

  // ditch the types we aren't keeping
  const isKeepType = type => keepTypes.has(type.id) || keepExtraTypes?.(type);
  metadata.value.lookup.types = metadata.value.lookup.types.filter(isKeepType);
};
const addDependentTypes = (metadata, keepTypes, types) => {
  for (const typeIndex of types) {
    const type = metadata.value.lookup.types[typeIndex];
    if (!type) {
      log.warn(`Unable to find type with index ${typeIndex}`);
      continue;
    }
    keepTypes.add(type.id);
    if (type?.type?.def?.__kind === "Array") addDependentTypes(metadata, keepTypes, [type.type.def.value.type]);
    if (type?.type?.def?.__kind === "Compact") addDependentTypes(metadata, keepTypes, [type.type.def.value.type]);
    if (type?.type?.def?.__kind === "Composite") addDependentTypes(metadata, keepTypes, type.type.def.value.fields.map(({
      type
    }) => type));
    if (type?.type?.def?.__kind === "Sequence") addDependentTypes(metadata, keepTypes, [type.type.def.value.type]);
    if (type?.type?.def?.__kind === "Tuple") addDependentTypes(metadata, keepTypes, type.type.def.value.map(type => type));
    if (type?.type?.def?.__kind === "Variant") addDependentTypes(metadata, keepTypes, type.type.def.value.variants.flatMap(({
      fields
    }) => fields.map(({
      type
    }) => type)));
  }
};

suppressPortableRegistryConsoleWarnings();

export { addDependentTypes, filterMetadataPalletsAndItems, metadataIsV14, mutateMetadata };
